/*
 * The landing screen. It exists to answer four questions in one look:
 * where are we, what have we run, how are we going, and what is in the garage.
 *
 * The track panel shows TODAY'S date, not the last session's - it answers
 * "where are we now", and a stale date at the top of the screen at a meeting
 * is worse than none.
 */

import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { CalendarDays, Gauge, MapPin, Plus, Timer, TrendingUp } from 'lucide-react'
import { api } from '../lib/api'
import type { CategoryDef, KartEvent, SessionSummary, TrackRow } from '../lib/api'
import { lapTime, longDate, shortDate } from '../lib/format'
import { Empty, LapChart, Metric, SessionRow, Spinner } from '../components/bits'

export function Dashboard() {
  const sessions = useQuery({
    queryKey: ['sessions', 'recent'],
    queryFn: () => api.get<{ total: number; sessions: SessionSummary[] }>(
      '/api/sessions?limit=12'),
  })
  const tracks = useQuery({
    queryKey: ['tracks'],
    queryFn: () => api.get<{ tracks: TrackRow[] }>('/api/tracks'),
  })
  const garage = useQuery({
    queryKey: ['garage'],
    queryFn: () => api.get<{ categories: CategoryDef[] }>('/api/garage'),
  })
  const events = useQuery({
    queryKey: ['events'],
    queryFn: () => api.get<{ events: KartEvent[] }>('/api/events'),
  })

  if (sessions.isLoading || tracks.isLoading) return <div className="page"><Spinner /></div>

  const rows = sessions.data?.sessions ?? []
  const allTracks = tracks.data?.tracks ?? []
  const timed = rows.filter((s) => s.best_lap_s)
  const best = timed.length
    ? timed.reduce((a, b) => (a.best_lap_s! <= b.best_lap_s! ? a : b))
    : null
  const last = rows[0]

  // Today's event decides the current track, so nobody has to select one.
  const today = new Date()
  const current = (events.data?.events ?? []).find((e) => {
    if (!e.start_date) return false
    const start = new Date(e.start_date)
    const end = e.end_date ? new Date(e.end_date) : start
    return today >= new Date(start.toDateString())
      && today <= new Date(new Date(end).toDateString() + ' 23:59:59')
  })
  const currentTrack = current?.track ?? last?.track ?? null

  // Oldest first, so the trend line reads left to right.
  const chart = [...timed].reverse().map((s) => ({
    id: s.id, label: shortDate(s.started_at, s.timezone), value: s.best_lap_s!,
  }))
  const improving = chart.length > 1 && chart[chart.length - 1]!.value < chart[0]!.value

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1>Dashboard</h1>
          <div className="sub">Track your kart setup sessions and performance</div>
        </div>
        <Link className="btn" to="/events"><Plus size={16} /> New session</Link>
      </div>

      <div className="card" style={{ marginBottom: 14 }}>
        <div className="between wrap">
          <div>
            <div className="label" style={{
              fontSize: 11, letterSpacing: '.09em', textTransform: 'uppercase',
              color: 'var(--ink-3)', fontWeight: 600,
            }}>
              {current ? current.event_type_label : 'Most recent track'}
            </div>
            <h2 style={{ fontSize: 24, margin: '4px 0 6px', letterSpacing: '-.02em' }}>
              {currentTrack ? currentTrack.name.toUpperCase() : 'NO SESSIONS YET'}
            </h2>
            <div className="row small muted wrap" style={{ gap: 16 }}>
              <span className="row" style={{ gap: 6 }}>
                <MapPin size={14} />
                {allTracks.find((t) => t.slug === currentTrack?.slug)?.state || 'Australia'}
              </span>
              <span className="row" style={{ gap: 6 }}>
                <CalendarDays size={14} /> {longDate(today)}
              </span>
            </div>
          </div>
          <Link className="btn ghost" to="/tracks">View tracks</Link>
        </div>
      </div>

      <div className="grid metrics" style={{ marginBottom: 14 }}>
        <Metric label="Total sessions" icon={Timer}
          value={sessions.data?.total ?? 0} foot="All time" />
        <Metric label="Best lap" icon={Timer} tone="green"
          value={best ? lapTime(best.best_lap_s) : '—'}
          foot={best ? `Run ${best.run_id}` : 'No timed laps yet'} />
        <Metric label="Tracks" icon={Gauge} value={allTracks.length}
          foot={allTracks.slice(0, 2).map((t) => t.name).join(', ') || '—'} />
        <Metric label="Last session" icon={CalendarDays}
          value={last ? shortDate(last.started_at, last.timezone) : '—'}
          foot={last?.track?.name ?? '—'} />
      </div>

      <div className="grid two-col" style={{ marginBottom: 14 }}>
        <div className="card">
          <div className="card-head">
            <h2>Recent sessions</h2>
            <Link className="small" to="/sessions">View all</Link>
          </div>
          {rows.length === 0 ? (
            <Empty title="No sessions yet"
              body="Sessions appear here automatically once the trackside agent uploads them from Race Studio." />
          ) : rows.slice(0, 6).map((s) => <SessionRow key={s.id} s={s} />)}
        </div>

        <div className="card">
          <div className="card-head">
            <h2>Setup overview</h2>
            <span className="badge">Best lap time</span>
          </div>
          <LapChart points={chart} />
          <div className="between" style={{ marginTop: 10 }}>
            {improving ? (
              <span className="row small" style={{ color: 'var(--green)', gap: 6 }}>
                <TrendingUp size={15} /> Improving trend
              </span>
            ) : <span className="small dim">Best lap by session</span>}
            <span className="small">
              Best: <b className="lap best">{best ? lapTime(best.best_lap_s) : '—'}</b>
            </span>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-head"><h2>Your garage</h2></div>
        <div className="grid three-col">
          {(garage.data?.categories ?? []).map((c) => (
            <Link key={c.key} to={`/garage/${c.key}`} className="card"
              style={{ background: 'var(--card-2)', textDecoration: 'none', color: 'inherit' }}>
              <h3>{c.plural}</h3>
              <div className="small muted" style={{ marginTop: 2 }}>
                {c.count ?? 0} {(c.count ?? 0) === 1 ? c.label.toLowerCase() : c.plural.toLowerCase()}
              </div>
              <div className="small" style={{ color: 'var(--blue-2)', marginTop: 10 }}>
                Manage {c.plural.toLowerCase()} →
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  )
}

